import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Divider
} from '@mui/material';
import DroneImageUpload from './DroneImageUpload';

const DRONE_MANUFACTURERS = [
  'DJI',
  'Autel Robotics',
  'Skydio',
  'Parrot',
  'Yuneec',
  'Freefly',
  'Insitu',
  'senseFly',
  'Other'
];

const DRONE_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'maintenance', label: 'In Maintenance' },
  { value: 'retired', label: 'Retired' },
  { value: 'lost', label: 'Lost' }
];

const EditDroneDialog = ({ open, onClose, onEdit, drone }) => {
  const [formData, setFormData] = useState({
    manufacturer: '',
    model: '',
    serialNumber: '',
    registrationNumber: '',
    nickname: '',
    status: 'active',
    purchaseDate: '',
    lastMaintenanceDate: '',
    totalFlightHours: 0,
    totalFlights: 0,
    notes: '',
    imageUrl: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Populate form when drone prop changes
  useEffect(() => {
    if (drone) {
      setFormData({
        manufacturer: drone.manufacturer || '',
        model: drone.model || '',
        serialNumber: drone.serialNumber || '',
        registrationNumber: drone.registrationNumber || '',
        nickname: drone.nickname || '',
        status: drone.status || 'active',
        purchaseDate: drone.purchaseDate ? drone.purchaseDate.split('T')[0] : '',
        lastMaintenanceDate: drone.lastMaintenanceDate ? drone.lastMaintenanceDate.split('T')[0] : '',
        totalFlightHours: drone.totalFlightHours || 0,
        totalFlights: drone.totalFlights || 0,
        notes: drone.notes || '',
        imageUrl: drone.imageUrl || ''
      });
    }
  }, [drone]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleImageUpload = (imageUrl) => {
    setFormData((prev) => ({
      ...prev,
      imageUrl: imageUrl || ''
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.manufacturer.trim()) {
      newErrors.manufacturer = 'Manufacturer is required';
    }
    if (!formData.model.trim()) {
      newErrors.model = 'Model is required';
    }
    if (!formData.serialNumber.trim()) {
      newErrors.serialNumber = 'Serial number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      await onEdit(formData);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setErrors({});
      setSubmitError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5">Edit Drone</Typography>
        <Typography variant="body2" color="text.secondary">
          Update drone information
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Manufacturer */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!errors.manufacturer}>
              <InputLabel>Manufacturer</InputLabel>
              <Select
                value={formData.manufacturer}
                onChange={handleChange('manufacturer')}
                label="Manufacturer"
              >
                {DRONE_MANUFACTURERS.map((manufacturer) => (
                  <MenuItem key={manufacturer} value={manufacturer}>
                    {manufacturer}
                  </MenuItem>
                ))}
              </Select>
              {errors.manufacturer && (
                <Typography variant="caption" color="error">
                  {errors.manufacturer}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Model */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Model"
              value={formData.model}
              onChange={handleChange('model')}
              error={!!errors.model}
              helperText={errors.model}
            />
          </Grid>

          {/* Serial Number */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Serial Number"
              value={formData.serialNumber}
              onChange={handleChange('serialNumber')}
              error={!!errors.serialNumber}
              helperText={errors.serialNumber || 'Must be unique'}
            />
          </Grid>

          {/* Registration Number */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="FAA Registration Number"
              value={formData.registrationNumber}
              onChange={handleChange('registrationNumber')}
            />
          </Grid>

          {/* Nickname */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nickname"
              value={formData.nickname}
              onChange={handleChange('nickname')}
              helperText="User-friendly name"
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={handleChange('status')}
                label="Status"
              >
                {DRONE_STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Purchase Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Purchase Date"
              value={formData.purchaseDate}
              onChange={handleChange('purchaseDate')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Last Maintenance Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Last Maintenance Date"
              value={formData.lastMaintenanceDate}
              onChange={handleChange('lastMaintenanceDate')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Flight Statistics
            </Typography>
          </Grid>

          {/* Total Flight Hours */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Total Flight Hours"
              value={(formData.totalFlightHours || 0).toFixed(2)}
              disabled
              helperText="Automatically calculated from flight logs"
            />
          </Grid>

          {/* Total Flights */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Total Flights"
              value={formData.totalFlights}
              disabled
              helperText="Automatically calculated from flight logs"
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Image Upload */}
          <Grid item xs={12}>
            <DroneImageUpload
              currentImageUrl={formData.imageUrl}
              onImageUpload={handleImageUpload}
              disabled={submitting}
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={formData.notes}
              onChange={handleChange('notes')}
              helperText="Additional notes about this drone"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDroneDialog;
